import { deleteRequest, getRequest, patchRequest, postRequest, RequestOpts } from 'features/common/api/requests'
import { MapComment, MapCommentWithResponses, MapCommentResponse } from '../types'

import settings from 'settings'
import { CommentFilter, CommentFilterType } from 'features/collaboration/types'
import { uriAddSearchParams } from 'features/common/utils/navigationUtils'

function baseUrl() {
  const { workspaceApiUrl } = settings
  return `${workspaceApiUrl}/maps`
}

//
// comments (aka threads or root comments)
//
export const getMapComments = async (
  mapId: string,
  commentFilter?: CommentFilter,
  opts?: RequestOpts
): Promise<Array<MapComment>> => {
  const url = uriAddSearchParams(`${baseUrl()}/${mapId}/comments`, {
    search: commentFilter?.searchText,
    filter: commentFilter?.filter === CommentFilterType.MyComments ? CommentFilterType.MyComments : undefined
  })
  return await getRequest(url, opts)
}

export const getMapComment = async (
  mapId: string,
  commentId: string,
  opts?: RequestOpts
): Promise<MapCommentWithResponses> => {
  return await getRequest(`${baseUrl()}/${mapId}/comments/${commentId}`, opts)
}

export type NewMapComment = Omit<
  MapComment,
  'id' | 'createdAt' | 'updatedAt' | 'resolved' | 'responsesCount' | 'author' | 'sequenceNumber'
>

export const createMapComment = async (comment: NewMapComment): Promise<MapComment> => {
  const { mapId, ...rest } = comment
  return await postRequest(`${baseUrl()}/${mapId}/comments`, rest)
}

export type MapCommentUpdate = Pick<MapComment, 'mapId' | 'id'> & Partial<MapComment>

export const updateMapComment = async (commentUpdate: MapCommentUpdate): Promise<MapComment> => {
  const { mapId, id: commentId, ...update } = commentUpdate
  return await patchRequest(`${baseUrl()}/${mapId}/comments/${commentId}`, update)
}

export const deleteMapComment = async ({
  mapId,
  id: commentId
}: Pick<MapComment, 'mapId' | 'id'>): Promise<unknown> => {
  return await deleteRequest(`${baseUrl()}/${mapId}/comments/${commentId}`)
}

export const resolveMapComment = async ({
  mapId,
  id: commentId
}: Pick<MapComment, 'mapId' | 'id'>): Promise<unknown> => {
  return await postRequest(`${baseUrl()}/${mapId}/comments/${commentId}/resolve`, {})
}

export const reopenMapComment = async ({
  mapId,
  id: commentId
}: Pick<MapComment, 'mapId' | 'id'>): Promise<unknown> => {
  return await postRequest(`${baseUrl()}/${mapId}/comments/${commentId}/reopen`, {})
}

export const markCommentsAsRead = async ({
  mapId,
  commentIds
}: {
  mapId: string
  commentIds: string[]
}): Promise<unknown> => {
  return await postRequest(`${baseUrl()}/${mapId}/comments/markAsRead`, { commentIds })
}

// comment respones (aka replies, or child comments)
//

export type NewMapCommentResponse = Omit<MapCommentResponse, 'id' | 'createdAt' | 'updatedAt' | 'author'>

export const createMapCommentResponse = async (
  newCommentResponse: NewMapCommentResponse
): Promise<MapCommentResponse> => {
  const { mapId, commentId, ...rest } = newCommentResponse
  return await postRequest(`${baseUrl()}/${mapId}/comments/${commentId}/responses`, rest)
}

export type MapCommentResponseUpdate = Pick<MapCommentResponse, 'mapId' | 'id' | 'commentId'> &
  Partial<MapCommentResponse>

export const updateMapCommentResponse = async (
  commentUpdate: MapCommentResponseUpdate
): Promise<MapCommentResponse> => {
  const { mapId, commentId, id: responseId, ...update } = commentUpdate
  return await patchRequest(`${baseUrl()}/${mapId}/comments/${commentId}/responses/${responseId}`, update)
}

export const deleteMapCommentResponse = async ({
  mapId,
  commentId,
  id: responseId
}: Pick<MapCommentResponse, 'mapId' | 'id' | 'commentId'>): Promise<unknown> => {
  return await deleteRequest(`${baseUrl()}/${mapId}/comments/${commentId}/responses/${responseId}`)
}
